/**
 * Tag every RUM event with the language the page is actually being read in.
 *
 * Why this is not in `beforeSend`: the SDK only lets `beforeSend` write the paths listed in
 * `modifiableFieldPathsByEvent`, and drops anything else silently. Global context is the
 * supported way to put a field on every event.
 *
 * Why it is not read once at startup: a visitor accepts the browser's translate prompt
 * *after* the page has loaded, which is the case we are trying to count. Reading at init
 * would record the untranslated state and miss every one of them. So the document element is
 * watched, and the context is refreshed when its language changes — which is exactly the
 * moment a translator acts.
 *
 * What this buys us: `context_translated` lands on every event, not just on errors, so the
 * share of sessions being translated becomes answerable. Until now we could only see
 * translated sessions *that crashed* (via `captureException`), which is a numerator with no
 * denominator — 7 crashes a week could be 7 out of 100 translated sessions or 7 out of
 * 10,000, and those two call for very different responses.
 */

/** Matches the locales the apps can serve. Anything else on `<html lang>` came from elsewhere. */
const SERVED_LOCALES = ['it', 'en'];

/** Chrome and Edge stamp one of these on `<html>` while their translator is active. */
const TRANSLATED_CLASSES = ['translated-ltr', 'translated-rtl'];

interface RumGlobalContext {
  setGlobalContextProperty: (key: string, value: unknown) => void;
}

function readPageLanguage(): { htmlLang: string | null; uiLang: string | null; translated: boolean } {
  const html = document.documentElement;
  const htmlLang = html.lang || null;
  const uiLang = typeof navigator === 'undefined' ? null : navigator.language;

  // Two independent signals, OR'd together, because neither is sufficient alone:
  //
  // 1. `<html lang>` holding a value outside the configured locales. next-i18next renders it
  //    from the route's locale and nothing in the app rewrites it, so a foreign value means a
  //    translator did. Its blind spot is it->en, where the translator writes a value that is
  //    also one of ours — and that is the commonest pair in our market (Italian content read
  //    in an English browser), so on its own this check would miss the bulk of what we are
  //    trying to size.
  // 2. The class Chrome and Edge add to `<html>`. It does not depend on the language pair, so
  //    it covers the it->en blind spot above.
  //
  // Not covered by either: a translator that neither rewrites `lang` nor marks the class.
  // Safari's is one, and we do have crashes from WebKit, so read the resulting share as a
  // floor rather than an exact count.
  const langIsForeign = !!htmlLang && !SERVED_LOCALES.includes(htmlLang.split('-')[0].toLowerCase());
  const hasTranslatedClass = TRANSLATED_CLASSES.some((name) => html.classList.contains(name));

  return { htmlLang, uiLang, translated: langIsForeign || hasTranslatedClass };
}

function publish(rum: RumGlobalContext): void {
  const { htmlLang, uiLang, translated } = readPageLanguage();
  rum.setGlobalContextProperty('htmlLang', htmlLang);
  rum.setGlobalContextProperty('uiLang', uiLang);
  rum.setGlobalContextProperty('translated', translated);
}

/**
 * Start tagging. Safe to call once the RUM SDK has initialized; a no-op on the server and
 * wherever `MutationObserver` is unavailable.
 *
 * Never throws: telemetry must not break the page it is measuring.
 */
export function trackPageTranslation(rum: RumGlobalContext): () => void {
  const noop = () => undefined;

  if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') {
    return noop;
  }

  try {
    publish(rum);

    // Watch both attributes the two signals live on, so whichever one the visitor's browser
    // touches refreshes the context at the moment they accept the translate prompt.
    const observer = new MutationObserver(() => publish(rum));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['lang', 'class'] });

    return () => observer.disconnect();
  } catch {
    return noop;
  }
}
